import { CONFIG } from '../config';
import { buildManifest, pool } from '../content/buildManifest';
import type { ContentSource } from '../content/source';
import type { Comando, FileRef, Manifest, ResolvedContent } from '../content/types';
import { backoffMs, blobToDataUrl, looksLikeImage, sleep, timeoutSignal } from '../lib/net';
import type { AssetStore } from '../storage/AssetStore';
import { kvCommit, kvGet, kvSet } from '../storage/db';

export type Phase = 'idle' | 'checking' | 'building' | 'downloading' | 'ready' | 'error';

export interface SyncStatus {
  phase: Phase;
  /** Archivos descargados / por descargar en la sincronización en curso */
  done: number;
  total: number;
  bytes: number;
  activeVersion: string | null;
  pendingVersion: string | null;
  lastCheckAt: number | null;
  lastSuccessAt: number | null;
  lastError: string | null;
  offline: boolean;
  /** Archivos del manifiesto activo que faltan en disco (debe ser 0) */
  missingLive: number;
}

export const KV = {
  active: 'manifest.active',
  pending: 'manifest.pending',
  lastCommand: 'command.lastId',
} as const;

class PermanentError extends Error {}

type Listener = (s: SyncStatus) => void;

export interface SyncDeps {
  store: AssetStore;
  source: ContentSource;
  report?: (err: unknown, context: Record<string, unknown>) => void;
  /** Para pruebas: reemplaza fetch */
  fetchImpl?: typeof fetch;
  /** Para pruebas: acorta esperas */
  sleepImpl?: (ms: number) => Promise<void>;
  gcDelayMs?: number;
}

/**
 * Motor de sincronización offline-first.
 *
 *   1. Versión: consulta barata (_updatedAt máximo + conteo). Si no cambió, no hace nada más.
 *   2. Manifiesto: proyectos + URLs transformadas + LQIP base64.
 *   3. Staging: descarga solo lo que falta (2 en paralelo, reintentos con backoff, verificación).
 *   4. Pendiente: con el 100 % verificado se guarda como `manifest.pending` en IndexedDB.
 *   5. Swap atómico (solo en modo reposo): staging → live, y en UNA transacción IDB
 *      active = pending. Después se borran los archivos que ya nadie referencia.
 *
 * Cualquier fallo antes del paso 5 deja la versión activa intacta.
 * Todas las operaciones que tocan almacenamiento pasan por un mutex: nunca se solapan.
 */
export class SyncEngine {
  status: SyncStatus = {
    phase: 'idle',
    done: 0,
    total: 0,
    bytes: 0,
    activeVersion: null,
    pendingVersion: null,
    lastCheckAt: null,
    lastSuccessAt: null,
    lastError: null,
    offline: false,
    missingLive: 0,
  };

  private listeners = new Set<Listener>();
  private lock: Promise<unknown> = Promise.resolve();
  private checking: Promise<void> | null = null;
  private resolved: ResolvedContent | null = null;
  private urls = new Map<string, string>();
  private needsRepair = false;
  private isIdle: () => boolean = () => false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private fetchImpl: typeof fetch;
  private sleep: (ms: number) => Promise<void>;

  onContent: (c: ResolvedContent) => void = () => {};
  onCommand: (c: Comando) => void = () => {};

  constructor(private deps: SyncDeps) {
    this.fetchImpl = deps.fetchImpl ?? ((...a) => fetch(...a));
    this.sleep = deps.sleepImpl ?? sleep;
  }

  /* ---------------- API pública ---------------- */

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  get content(): ResolvedContent | null {
    return this.resolved;
  }

  get store(): AssetStore {
    return this.deps.store;
  }

  /** Indica a la app si está en reposo (único momento en que se aplica una versión nueva) */
  setIdleProvider(fn: () => boolean) {
    this.isIdle = fn;
  }

  /** Arranque: promueve un pendiente (arrancar = reposo), carga el activo y verifica integridad */
  async boot(): Promise<ResolvedContent | null> {
    await this.deps.store.init();
    const pending = await kvGet<Manifest>(KV.pending);
    if (pending) {
      await this.withLock(() => this.commit(pending)).catch((e) => this.fail(e, 'boot-commit'));
    }
    if (!this.resolved) {
      const active = await kvGet<Manifest>(KV.active);
      if (active) await this.activate(active);
    }
    await this.verifyLive();
    this.scheduleGc();
    return this.resolved;
  }

  /** Inicia el ciclo: consulta ahora y luego cada CONFIG.pollMs */
  start() {
    if (this.started) return;
    this.started = true;
    window.addEventListener('online', () => this.kick(2000));
    // Si el WebView estuvo oculto/suspendido, sus temporizadores pudieron congelarse: reintenta al volver
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.kick(1000);
    });
    this.kick(0);
  }

  /** Fuerza una consulta (comando remoto "sincronizar" o panel de administración) */
  checkNow(force = false): Promise<void> {
    return this.check(force);
  }

  /** La app llama esto al entrar en reposo */
  async applyPendingIfIdle(): Promise<void> {
    if (!this.status.pendingVersion || !this.isIdle()) return;
    await this.withLock(async () => {
      const pending = await kvGet<Manifest>(KV.pending);
      if (pending && this.isIdle()) await this.commit(pending);
    }).catch((e) => this.fail(e, 'apply'));
  }

  /** Una imagen no cargó en la UI: se verifica el disco y, si falta algo, se repara */
  reportMissing(key: string) {
    if (this.needsRepair) return;
    this.deps.report?.(new Error('Imagen local no disponible'), { key });
    this.needsRepair = true;
    this.kick(5000);
  }

  /* ---------------- Ciclo de consulta ---------------- */

  private kick(delay: number) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.check(false).finally(() => {
        // Sin contenido todavía (primer arranque): reintenta rápido
        this.kick(this.resolved ? CONFIG.pollMs : 15_000);
      });
    }, delay);
  }

  private check(force: boolean): Promise<void> {
    if (this.checking) return this.checking;
    this.checking = this.withLock(() => this.runCheck(force))
      .catch((e) => this.fail(e, 'check'))
      .finally(() => {
        this.checking = null;
      });
    return this.checking;
  }

  private async runCheck(force: boolean) {
    this.set({ phase: 'checking', lastCheckAt: Date.now() });
    const info = await this.deps.source.fetchVersion();
    this.set({ offline: false });

    if (info.command) {
      const last = await kvGet<string>(KV.lastCommand);
      if (last !== info.command.comandoId) {
        // Se marca ANTES de ejecutar: un comando se ejecuta una sola vez aunque se reinicie el equipo
        await kvSet(KV.lastCommand, info.command.comandoId);
        if (info.command.comando === 'sincronizar') force = true;
        else this.onCommand(info.command.comando);
      }
    }

    const active = this.resolved?.manifest ?? null;
    const pending = await kvGet<Manifest>(KV.pending);
    if (!force && !this.needsRepair) {
      if (pending && pending.version === info.version) {
        this.set({ phase: 'ready', pendingVersion: pending.version, lastSuccessAt: Date.now(), lastError: null });
        return;
      }
      if (active && active.version === info.version) {
        this.set({ phase: 'idle', lastSuccessAt: Date.now(), lastError: null });
        return;
      }
    }

    this.set({ phase: 'building' });
    const raw = await this.deps.source.fetchContent();
    const manifest = await buildManifest(raw, {
      previous: active,
      fetchLqip: async (url) => blobToDataUrl(await this.download(url)),
    });
    if (!manifest.projects.length) throw new PermanentError('El contenido publicado no tiene proyectos válidos');

    await this.stage(manifest);
    const live = await this.deps.store.keys('live');
    if (active && manifest.version === active.version && manifest.files.every((f) => live.has(f.key))) {
      // Sincronización forzada sin cambios: todo verificado, nada que activar
      await this.deps.store.clear('staging');
      this.set({ phase: 'idle', lastSuccessAt: Date.now(), lastError: null });
      return;
    }
    await kvSet(KV.pending, manifest);
    this.needsRepair = false;
    this.set({ phase: 'ready', pendingVersion: manifest.version, lastSuccessAt: Date.now(), lastError: null });

    // Primer arranque (nada que proteger) o ya en reposo: se activa ya
    if (!active || this.isIdle()) await this.commit(manifest);
  }

  /** Descarga a staging todo lo que no esté ya en disco y verifica el 100 % */
  private async stage(m: Manifest) {
    const { store } = this.deps;
    const live = await store.keys('live');
    const staged = await store.keys('staging');
    const need = uniq(m.files).filter((f) => !live.has(f.key) && !staged.has(f.key));
    this.set({ phase: 'downloading', done: 0, total: need.length, bytes: 0 });

    await pool(need, CONFIG.maxParallel, async (f) => {
      const blob = await this.download(f.url);
      await store.put('staging', f.key, blob);
      this.set({ done: this.status.done + 1, bytes: this.status.bytes + blob.size });
    });

    const live2 = await store.keys('live');
    const staged2 = await store.keys('staging');
    const missing = m.files.filter((f) => !live2.has(f.key) && !staged2.has(f.key));
    if (missing.length) throw new Error(`Verificación: faltan ${missing.length} archivos tras la descarga`);

    const needed = new Set(m.files.map((f) => f.key));
    await store.remove('staging', [...staged2].filter((k) => !needed.has(k)));
  }

  /** Descarga con verificación: status 200, tamaño > 0, content-length coincidente y firma de imagen */
  private async download(url: string): Promise<Blob> {
    let lastErr: unknown = null;
    for (let attempt = 0; attempt < CONFIG.maxAttempts; attempt++) {
      const { signal, done } = timeoutSignal(CONFIG.fileTimeoutMs);
      try {
        const res = await this.fetchImpl(url, { cache: 'no-store', mode: 'cors', credentials: 'omit', signal });
        if (res.status !== 200) {
          const permanent = res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429;
          throw new (permanent ? PermanentError : Error)(`HTTP ${res.status} ${url}`);
        }
        const declared = res.headers.get('content-length');
        const encoded = res.headers.get('content-encoding');
        const blob = await res.blob();
        if (blob.size === 0) throw new Error(`Archivo vacío ${url}`);
        if (declared && !encoded && Number(declared) !== blob.size) {
          throw new Error(`Tamaño ${blob.size} ≠ content-length ${declared} en ${url}`);
        }
        if (!(await looksLikeImage(blob))) throw new Error(`No es una imagen válida: ${url}`);
        return blob;
      } catch (e) {
        lastErr = e;
        if (e instanceof PermanentError) throw e;
        if (attempt < CONFIG.maxAttempts - 1) await this.sleep(backoffMs(attempt));
      } finally {
        done();
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
  }

  /* ---------------- Swap atómico ---------------- */

  private async commit(m: Manifest) {
    const { store } = this.deps;
    const keys = uniq(m.files).map((f) => f.key);
    const staged = await store.keys('staging');
    await store.promote(keys.filter((k) => staged.has(k)));

    const live = await store.keys('live');
    const missing = keys.filter((k) => !live.has(k));
    if (missing.length) {
      // El pendiente no es activable (p. ej. el sistema purgó staging): se descarta y se repara
      await kvCommit({}, [KV.pending]);
      this.needsRepair = true;
      this.set({ pendingVersion: null });
      throw new Error(`Swap cancelado: faltan ${missing.length} archivos en live`);
    }

    await kvCommit({ [KV.active]: m }, [KV.pending]); // ← el swap: una sola transacción
    await store.clear('staging');
    this.set({ pendingVersion: null, missingLive: 0 });
    await this.activate(m);
    this.scheduleGc();
  }

  private async activate(m: Manifest) {
    const old = this.urls;
    this.urls = await this.deps.store.resolveUrls(uniq(m.files).map((f) => f.key));
    const urls = this.urls;
    this.resolved = { manifest: m, src: (f: FileRef | null | undefined) => (f ? urls.get(f.key) || '' : '') };
    this.set({ activeVersion: m.version });
    this.onContent(this.resolved);
    // La UI ya pinta las URLs nuevas; las antiguas se liberan con margen
    setTimeout(() => this.deps.store.releaseUrls(old), 30_000);
  }

  private async verifyLive() {
    const m = this.resolved?.manifest;
    if (!m) return;
    const live = await this.deps.store.keys('live');
    const missing = m.files.filter((f) => !live.has(f.key)).length;
    this.set({ missingLive: missing });
    if (missing) {
      this.needsRepair = true;
      this.deps.report?.(new Error('Integridad: faltan archivos en live al arrancar'), { missing });
    }
  }

  /** Borra de live lo que ya no referencia ni el activo ni el pendiente */
  private scheduleGc() {
    setTimeout(() => {
      this.withLock(async () => {
        const active = await kvGet<Manifest>(KV.active);
        if (!active) return;
        const pending = await kvGet<Manifest>(KV.pending);
        const keep = new Set([...active.files, ...(pending?.files || [])].map((f) => f.key));
        const live = await this.deps.store.keys('live');
        const drop = [...live].filter((k) => !keep.has(k));
        if (drop.length) await this.deps.store.remove('live', drop);
      }).catch((e) => this.deps.report?.(e, { op: 'gc' }));
    }, this.deps.gcDelayMs ?? 20_000);
  }

  /* ---------------- Utilidades ---------------- */

  private withLock<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.lock.then(fn, fn);
    this.lock = run.catch(() => undefined);
    return run;
  }

  private fail(e: unknown, op: string) {
    const msg = e instanceof Error ? e.message : String(e);
    const offline = e instanceof TypeError || (e instanceof DOMException && e.name === 'AbortError');
    this.set({ phase: 'error', lastError: msg, offline });
    // Sin red es un estado normal (Wi-Fi caída): no se reporta a Sentry en cada ciclo
    if (!offline) this.deps.report?.(e, { op });
  }

  private set(patch: Partial<SyncStatus>) {
    this.status = { ...this.status, ...patch };
    for (const l of this.listeners) l(this.status);
  }
}

function uniq(files: FileRef[]): FileRef[] {
  const seen = new Set<string>();
  return files.filter((f) => (seen.has(f.key) ? false : (seen.add(f.key), true)));
}
